import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { ScoreBadge, PriorityPill } from "@/components/app/ScoreBadge";
import { Button } from "@/components/ui/button";
import { Lightbulb, Layers as LayersIcon } from "lucide-react";
import { toast } from "sonner";
import { InfoTip } from "@/components/app/InfoTip";

const STATUSES = ["Open", "In Progress", "Completed", "Rejected", "On Hold"];

export default function ProductGaps() {
  const { selectedBrandId } = useBrands();
  const [items, setItems] = useState([]);
  const load = () => {
    if (!selectedBrandId) return;
    api.get(`/product-gaps?brand_id=${selectedBrandId}`).then((r) => setItems(r.data.items || []));
  };
  useEffect(load, [selectedBrandId]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/product-gaps/${id}`, { status });
      setItems((arr) => arr.map((it) => (it.id === id ? { ...it, status } : it)));
    } catch (e) { toast.error(extractError(e)); }
  };

  const groupedByCluster = items.reduce((acc, g) => {
    const k = g.cluster_name || "Unclassified";
    if (!acc[k]) acc[k] = [];
    acc[k].push(g);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Execution</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 inline-flex items-center gap-2">
          Product Gap Analysis
          <InfoTip term="PRODUCT_GAP" />
        </h1>
        <p className="mt-1 text-sm text-slate-500">{items.length} high-demand keywords with no matching SKU · grouped by cluster.</p>
      </div>

      {!items.length ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">
          No product gaps detected from the latest upload.
        </div>
      ) : (
        Object.entries(groupedByCluster).map(([cluster, gaps]) => (
          <div key={cluster} className="space-y-3">
            <div className="flex items-center gap-2">
              <LayersIcon className="h-4 w-4 text-slate-400" />
              <h2 className="text-base font-semibold tracking-tight text-slate-900">{cluster}</h2>
              <span className="text-xs text-slate-500">· {gaps.length} gaps</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`gap-cluster-${cluster}`}>
              {gaps.map((g) => (
                <div key={g.id} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <span className="text-[11px] uppercase tracking-widest text-slate-500">Gap keyword</span>
                      <h3 className="mt-0.5 text-base font-semibold tracking-tight text-slate-900">{g.gap_keyword}</h3>
                    </div>
                    <PriorityPill priority={g.priority} />
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2">
                    <Stat label={<span className="inline-flex items-center gap-1">Demand <InfoTip term="DEMAND" /></span>} value={g.demand_score} score />
                    <Stat label={<span className="inline-flex items-center gap-1">Rank <InfoTip term="SFR" /></span>} value={g.search_rank?.toLocaleString() || "—"} />
                    <Stat label="Weeks" value={g.repeated_weeks_count} />
                  </div>
                  <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 flex items-start gap-2">
                    <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                    <span>{g.suggested_product_idea}</span>
                  </div>
                  <div className="mt-3 text-xs text-slate-500 line-clamp-2">{g.reason}</div>
                  <div className="mt-4 flex items-center justify-between">
                    <select value={g.status} onChange={(e) => updateStatus(g.id, e.target.value)} className="text-xs rounded-md border border-slate-200 bg-white px-2 py-1" data-testid={`gap-status-${g.id}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function Stat({ label, value, score }) {
  return (
    <div className="rounded-lg bg-slate-50 px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      {score ? <ScoreBadge score={value} className="mt-0.5" /> : <div className="text-sm font-semibold text-slate-900">{value}</div>}
    </div>
  );
}
